/**
 * AI proxy frontend client (Этап 6 backend, 2026-05-25, см. doc 19).
 *
 * Frontend часть:
 *   - Никогда не имеет API key (он в Worker secret)
 *   - Шлёт `{ anon_id, prompt_kind, context }` на /ai/generate
 *   - Backend строит canned prompt из templates → calls Claude →
 *     returns text
 *
 * Use cases (frontend выбирает kind, не free text):
 *   - 'welcome' — personalized приветствие после name save
 *     context: { name, epoch }
 *   - 'phrase_variant' — variation существующей фразы (для расширения
 *     контента в будущем)
 *     context: { base_text, layer }
 *
 * Gates:
 *   - VITE_API_BASE set
 *   - canSendTelemetry() — без consent не шлём (это и tracking данные)
 *
 * Failure modes (все graceful):
 *   - 503 ai_not_configured → JB ещё не поставил ANTHROPIC_API_KEY
 *   - 429 rate_limited → больше 10/hour для этого anon_id
 *   - 502 ai_call_failed → Anthropic API down/overloaded
 *   - Network error → silent null
 */
import { getAnonId } from './anonId.js';
import { canSendTelemetry } from './consent.js';

export type PromptKind = 'welcome' | 'phrase_variant';

interface GenerateResponse {
  readonly ok: true;
  readonly text: string;
}

interface ErrorResponse {
  readonly error: string;
}

function getApiBase(): string | null {
  const base = import.meta.env.VITE_API_BASE ?? '';
  if (base.length === 0) return null;
  return base.replace(/\/$/, '');
}

/**
 * Запрос generation. Возвращает text или null если что-то пошло не так
 * (UI должен gracefully fall back на static content без блокировки UX).
 *
 * Все ошибки тихие — log warn, callable side просто получает null.
 */
export async function generateText(
  kind: PromptKind,
  context: Record<string, string | number>,
): Promise<string | null> {
  const base = getApiBase();
  if (base === null) return null;
  if (!canSendTelemetry()) return null;

  const anonId = getAnonId();
  if (anonId === 'ssr' || anonId === 'no-storage') return null;

  try {
    const res = await fetch(`${base}/ai/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anon_id: anonId,
        prompt_kind: kind,
        context,
      }),
    });
    if (res.status === 503) {
      // AI ещё не configured (нет ANTHROPIC_API_KEY) — silent.
      return null;
    }
    if (res.status === 429) {
      console.warn('[ai-proxy] rate limited');
      return null;
    }
    if (!res.ok) {
      console.warn('[ai-proxy] failed:', res.status);
      return null;
    }
    const data = (await res.json()) as GenerateResponse | ErrorResponse;
    if (!('ok' in data)) {
      console.warn('[ai-proxy] error response:', data);
      return null;
    }
    if (typeof data.text !== 'string' || data.text.length === 0) return null;
    return data.text;
  } catch (err) {
    console.warn('[ai-proxy] exception:', err);
    return null;
  }
}
